import React, { useEffect, useRef, useState } from 'react';
import { FastField, Form, Formik } from 'formik';
import _ from 'lodash';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';

import Spinner from '../../components/Spinner/Spinner';
import CheckoutBreadCrumb from './CheckoutBreadCrumb';
import { AddressHeader } from './CheckoutComponents/AddressDetailComponents/AddressHeader';
import AddressBlock from './CheckoutComponents/AddressDetailComponents/AddressBlock';
import { apiClient, useAsyncFunction } from '../../../helpers/apiHelperV2';
import { sessionIdSelector, userResponseSelector, userSelector } from '../../../modules/selectors/auth';
import ReduxSuspense from '../../components/ReduxSuspense/ReduxSuspense';
import PersonalInformation, { PersonalInformationSchemaFields } from '../../components/form-fragments/PersonalInformation';

import Input from '../../components/form-elements/Input';
import { standortUrlSelector } from '../../../modules/selectors/url';
import Button from '../../components/form-elements/Button';
import StandortRedirect from '../../components/VHost/StandortRedirect';
import { paypalOneClickSessionSelector } from '../../../modules/selectors/paypalOneClickSession';
import { InternalLink } from '../../components/form-elements/Link';
// User Schemas:
// - account create: new
// - initCheckout: new => old
// - checkout/address import from server: old => new
// - checkout/address export to server: new => old
// - downstream consumers (backend/emails): old

const defaultNewAddress = {
    isCompany: false,
    company: '',
    salutation: '',
    titel: '',
    firstName: '',
    lastName: '',
    street: '',
    additionalAddress: '',
    zipCode: '',
    city: '',
    country: 'Deutschland',
};

function oldAddressToNewAddress({
    isCompany = defaultNewAddress.isCompany,
    companyName = defaultNewAddress.company,
    salutation = defaultNewAddress.salutation,
    titel = defaultNewAddress.titel,
    firstName = defaultNewAddress.firstName,
    lastName = defaultNewAddress.lastName,
    street = defaultNewAddress.street,
    additionalAddressInfo = defaultNewAddress.additionalAddress,
    zipCode = defaultNewAddress.zipCode,
    city = defaultNewAddress.city,
    country = defaultNewAddress.country,
}) {
    return {
        isCompany,
        company: companyName,
        salutation,
        titel,
        firstName,
        lastName,
        street,
        additionalAddress: additionalAddressInfo,
        zipCode,
        city,
        country,
    };
}

function newAddressToOldAddress({
    isCompany,
    company,
    salutation,
    titel,
    firstName,
    lastName,
    street,
    additionalAddress,
    zipCode,
    city,
    country,
}) {
    return {
        isCompany,
        companyName: company,
        salutation,
        titel,
        firstName,
        lastName,
        street,
        additionalAddressInfo: additionalAddress,
        zipCode,
        city,
        country,
    };
}

export const UserDataSchema = Yup.object().shape({
    invoiceAddress: Yup.object().shape(PersonalInformationSchemaFields),
    deliveryAddress: Yup.object().when('deliveryToInvoice', deliveryToInvoice => {
        if (deliveryToInvoice) {
            return Yup.mixed();
        } else {
            return Yup.object().shape(PersonalInformationSchemaFields);
        }
    }),
    infoEmail: Yup.string().email('Bitte gültiges E-Mail Format eingeben.').required('Bitte E-Mail-Adresse eingeben.'),
    infoPhoneNumber: Yup.string(),
    deliveryToInvoice: Yup.boolean(),
});

function Adresse({ history }) {
    const userResponse = useSelector(userResponseSelector);
    const user = useSelector(userSelector);
    const isLoggedInUser = Boolean(user?._id);
    const sessionId = useSelector(sessionIdSelector);
    const standortUrl = useSelector(standortUrlSelector);
    const [skipEmailValidationOnce, setSkipEmailValidationOnce] = useState(false);
    const [emailValidationError, setEmailValidationError] = useState(false);
    const [lastValidatedEmail, setLastValidatedEmail] = useState('');
    const emailValidationErrorRef = useRef(null);

    useEffect(() => {
        if (user?._id) {
            setSkipEmailValidationOnce(true);
            setEmailValidationError(false);
            setLastValidatedEmail(user.email);
        }
    }, [user?._id, user?.email]);

    useEffect(() => {
        if (!emailValidationError) return;
        emailValidationErrorRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
        });
    }, [emailValidationError]);

    const paypalOneClickSessionId = useSelector(paypalOneClickSessionSelector)
    const getSessionId = paypalOneClickSessionId ? paypalOneClickSessionId : sessionId

    const initialValuesResponse = useAsyncFunction(async () => {
        const {
            data: { data = {} },
        } = await apiClient.post('/api/v1/checkout/adresse', {
            sessionId: getSessionId,
            standortUrl: standortUrl,
        });

        if (data.redirect && typeof data.redirect !== 'boolean') return { redirectUrl: data.redirect };
        if (data.redirectUrl) return { redirectUrl: data.redirectUrl };
        const checkout = data.checkoutData || {};
        const steps = checkout.checkoutSteps ?? [];
        const nextStep = steps[steps.findIndex(s => s.active) + 1] || {};

        const initialValues = {
            infoEmail: user?.email ?? checkout.userdata?.infoEmail ?? '',
            infoPhoneNumber: checkout.userdata?.infoPhoneNumber ?? '',
            invoiceAddress: oldAddressToNewAddress(user ?? checkout.userdata?.invoiceAddress ?? {}),
            deliveryAddress: oldAddressToNewAddress(user ?? checkout.userdata?.deliveryAddress ?? {}),
            deliveryToInvoice: (checkout.userdata?.deliveryType ?? 'invoiceAddress') === 'invoiceAddress',
        };

        return {
            initialValues,
            steps,
            nextStep,
            checkout,
        };
    }, [standortUrl, getSessionId, user]);

    return (
        <div className="checkout_wrapper">
            <ReduxSuspense response={initialValuesResponse}>
                {({ initialValues, steps, nextStep, redirectUrl }) => {
                    if (redirectUrl) return <StandortRedirect to={redirectUrl} push />;
                    return (
                        <div className="checkout">
                            <CheckoutBreadCrumb partials={steps} standortUrl={standortUrl} title="Kasse" />
                            <Formik
                                validationSchema={UserDataSchema}
                                initialValues={initialValues}
                                enableReinitialize={true}
                                onSubmit={async (values, actions) => {
                                    try {
                                        const normalizedEmail = (values.infoEmail || '').trim().toLowerCase();
                                        const shouldValidateEmail = !skipEmailValidationOnce || normalizedEmail !== lastValidatedEmail;

                                        if (shouldValidateEmail) {
                                            try {
                                                await apiClient.post('/api/v1/validation/email', { email: values.infoEmail, standortUrl });
                                            } catch (err) {
                                                const status = err?.response?.status;
                                                if (status === 400) {
                                                    setLastValidatedEmail(normalizedEmail);
                                                    setSkipEmailValidationOnce(true);
                                                    setEmailValidationError(true);
                                                    window.UIkit.notification(
                                                        'Zu dieser E-Mail-Adresse besteht bereits ein Kundenkonto.<br/>Sie können sich anmelden oder als Gast fortfahren.',
                                                        {
                                                            status: 'danger',
                                                            pos: 'top-right',
                                                        }
                                                    );
                                                    return;
                                                }
                                                window.UIkit.notification(
                                                    'Es ist ein Fehler aufgetreten, bitte versuchen Sie es später erneut.',
                                                    {
                                                        status: 'error',
                                                        pos: 'top-right',
                                                    }
                                                );
                                            }
                                        }

                                        if (values.deliveryToInvoice) {
                                            values.deliveryAddress = values.invoiceAddress;
                                        }

                                        const checkoutData = {
                                            userdata: {
                                                deliveryAddress: newAddressToOldAddress(values.deliveryAddress),
                                                invoiceAddress: newAddressToOldAddress(values.invoiceAddress),
                                                deliveryType: values.deliveryToInvoice ? 'invoiceAddress' : 'deliveryAddress',
                                                infoEmail: values.infoEmail,
                                                infoPhoneNumber: values.infoPhoneNumber,
                                            },
                                            checkoutSteps: steps.map(s => ({ key: s.key, title: s.title })),
                                        };
                                        const partial = steps.find(s => s.active);

                                        await apiClient.post('/api/v1/PersistCheckout', { checkoutData, sessionId: getSessionId });

                                        const {
                                            data: { data },
                                        } = await apiClient.post(`/api/v1/getNextCheckoutUrl/${partial.key}`, {
                                            sessionId: getSessionId,
                                        });

                                        return history.push(data.redirectUrl);
                                    } catch (err) {
                                        console.error(err);
                                        window.UIkit.notification('Es ist ein Fehler aufgetreten, bitte versuchen Sie es später erneut.', {
                                            status: 'error',
                                            pos: 'top-right',
                                        });
                                    } finally {
                                        actions.setSubmitting(false);
                                    }
                                }}
                            >
                                {({ values, errors, setFieldValue, isSubmitting, touched, setFieldTouched, handleBlur }) => {
                                    return (
                                        <Form>
                                            {isSubmitting && <Spinner />}
                                            {emailValidationError && (
                                                <div
                                                    ref={emailValidationErrorRef}
                                                    id="email-validation-error"
                                                    className="uk-text-danger"
                                                    role="alert"
                                                    aria-live="assertive"
                                                >
                                                    Zu dieser E-Mail-Adresse besteht bereits ein Kundenkonto. Sie können sich <InternalLink to="/login?context=checkout" ariaLabel="Anmelden" className="email-validation-link">anmelden</InternalLink> oder als Gast fortfahren.
                                                </div>
                                            )}
                                            <ReduxSuspense response={userResponse}>
                                                {user => (
                                                    <AddressHeader
                                                        icon="fa-file"
                                                        title="Rechnungsadresse"
                                                        hideButton={!user}
                                                        hiddenChildren={<AddressBlock address={values.invoiceAddress} />}
                                                        hasError={!_.isEmpty(errors.invoiceAddress)}
                                                    >
                                                        <PersonalInformation
                                                            errors={errors.invoiceAddress ?? {}}
                                                            touched={touched.invoiceAddress ?? {}}
                                                            values={values.invoiceAddress}
                                                            onChange={({ name, value }) => setFieldValue(`invoiceAddress.${name}`, value)}
                                                            onBlur={({ target: { name } }) => setFieldTouched(`invoiceAddress.${name}`)}
                                                        />
                                                    </AddressHeader>
                                                )}
                                            </ReduxSuspense>
                                            <div className="uk-grid-small uk-child-width-auto uk-grid">
                                                <label>
                                                    <FastField
                                                        className="uk-checkbox uk-margin-remove"
                                                        type="checkbox"
                                                        name="deliveryToInvoice"
                                                        checked={values.deliveryToInvoice}
                                                    />{' '}
                                                    Lieferung an die Rechnungsadresse
                                                </label>
                                            </div>
                                            {values.deliveryToInvoice ? (
                                                <AddressHeader icon="fa-truck" title="Lieferadresse" hideButton>
                                                    <div>
                                                        <i className="fa fa-check" /> Wie Rechnungsadresse
                                                    </div>
                                                </AddressHeader>
                                            ) : (
                                                <AddressHeader
                                                    icon="fa-truck"
                                                    title="Lieferadresse"
                                                    hideButton={_.isEqual(values.deliveryAddress, defaultNewAddress)}
                                                    hiddenChildren={<AddressBlock address={values.deliveryAddress} />}
                                                >
                                                    <PersonalInformation
                                                        errors={errors.deliveryAddress ?? {}}
                                                        touched={touched.deliveryAddress ?? {}}
                                                        values={values.deliveryAddress}
                                                        onChange={({ name, value }) => setFieldValue(`deliveryAddress.${name}`, value)}
                                                        onBlur={({ target: { name } }) => setFieldTouched(`deliveryAddress.${name}`)}
                                                    />
                                                </AddressHeader>
                                            )}
                                            <AddressHeader icon="fa-user" title="Kontaktdaten" hideButton>
                                                <Input
                                                    name="infoPhoneNumber"
                                                    id="infoPhoneNumber"
                                                    value={values.infoPhoneNumber}
                                                    label="Telefonnr. (tagsüber)"
                                                    placeholder="Telefonnummer (tagsüber)"
                                                    onChange={({ name, value }) => setFieldValue(name, value)}
                                                    onBlur={handleBlur}
                                                    error={
                                                        touched.infoPhoneNumber && errors.infoPhoneNumber
                                                            ? errors.infoPhoneNumber
                                                            : undefined
                                                    }
                                                    aria-required="false"
                                                />
                                                <Input
                                                    name="infoEmail"
                                                    id="infoEmail"
                                                    value={values.infoEmail}
                                                    label="E-Mail Adresse *"
                                                    placeholder="E-Mail Adresse"
                                                    disabled={isLoggedInUser}
                                                    aria-describedby={emailValidationError ? 'email-validation-error' : undefined}
                                                    onChange={({ name, value }) => {
                                                        setFieldValue(name, value);
                                                        const normalizedValue = (value || '').trim().toLowerCase();
                                                        if (normalizedValue !== lastValidatedEmail) {
                                                            setSkipEmailValidationOnce(false);
                                                            setEmailValidationError(false);
                                                        }
                                                    }}
                                                    onBlur={handleBlur}
                                                    error={touched.infoEmail && errors.infoEmail ? errors.infoEmail : undefined}
                                                    aria-required="true"
                                                />
                                            </AddressHeader>
                                            <Button fullWidth type="submit">
                                                WEITER ZUR {nextStep.title}
                                            </Button>
                                        </Form>
                                    );
                                }}
                            </Formik>
                            <div className="required-text uk-text uk-margin-small-top">
                                <p>* Pflichtfeld</p>
                            </div>
                        </div>
                    );
                }}
            </ReduxSuspense>
        </div>
    );
}

export default Adresse;
